/**
 * TrustScoreBadge Component
 * 
 * Simple, mobile-friendly trust badge for providers.
 * Shows trust level label and optional score.
 * 
 * Labels:
 * - 0-30: New
 * - 30-60: Reliable
 * - 60-80: Trusted
 * - 80-100: Top Provider
 */

import React from 'react';
import { Shield, Star, Award, UserCheck } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

// Trust level configurations
const TRUST_LEVELS = {
  new: {
    min: 0,
    max: 30,
    label: 'trustBadge.new',
    fallbackLabel: 'New',
    icon: UserCheck,
    bgClass: 'bg-gray-100 text-gray-700 border-gray-200',
    iconClass: 'text-gray-500'
  },
  reliable: {
    min: 30,
    max: 60,
    label: 'trustBadge.reliable',
    fallbackLabel: 'Reliable',
    icon: Shield,
    bgClass: 'bg-blue-50 text-blue-700 border-blue-200',
    iconClass: 'text-blue-500'
  },
  trusted: {
    min: 60,
    max: 80,
    label: 'trustBadge.trusted',
    fallbackLabel: 'Trusted',
    icon: Star,
    bgClass: 'bg-green-50 text-green-700 border-green-200',
    iconClass: 'text-green-500'
  },
  top_provider: {
    min: 80,
    max: 100,
    label: 'trustBadge.topProvider',
    fallbackLabel: 'Top Provider',
    icon: Award,
    bgClass: 'bg-gradient-to-r from-amber-50 to-yellow-50 text-amber-800 border-amber-300',
    iconClass: 'text-amber-500'
  }
};

function getTrustLevel(score) {
  if (score >= 80) return TRUST_LEVELS.top_provider;
  if (score >= 60) return TRUST_LEVELS.trusted;
  if (score >= 30) return TRUST_LEVELS.reliable;
  return TRUST_LEVELS.new;
}

export function TrustScoreBadge({ 
  score = 0, 
  showScore = false,
  size = 'default', // 'sm' | 'default' | 'lg'
  className = '',
  t // translation function (optional)
}) {
  const level = getTrustLevel(score);
  const Icon = level.icon;
  
  const sizeClasses = {
    sm: 'text-xs px-1.5 py-0.5 gap-0.5',
    default: 'text-sm px-2 py-1 gap-1',
    lg: 'text-base px-3 py-1.5 gap-1.5'
  };
  
  const iconSizes = {
    sm: 'h-3 w-3',
    default: 'h-3.5 w-3.5',
    lg: 'h-4 w-4'
  };
  
  const label = t ? t(level.label) : level.fallbackLabel;
  
  return (
    <Badge 
      variant="outline"
      className={cn(
        'inline-flex items-center font-medium border',
        level.bgClass,
        sizeClasses[size],
        className
      )}
      data-testid="trust-score-badge"
      data-trust-level={score >= 80 ? 'top_provider' : score >= 60 ? 'trusted' : score >= 30 ? 'reliable' : 'new'}
    >
      <Icon className={cn(iconSizes[size], level.iconClass)} />
      <span data-testid="trust-badge-label">{label}</span>
      {showScore && (
        <span className="opacity-70" data-testid="trust-badge-score">({Math.round(score)})</span>
      )}
    </Badge>
  );
}

export default TrustScoreBadge;
